import React, { useRef, useState } from 'react';
import {
  FaPlay,
  FaPause,
  FaForwardFast,
  FaBackwardFast,
  FaHeadphones,
} from 'react-icons/fa6';

function formatTime(s) {
  if (!s || !isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

const SPEEDS = [1, 1.25, 1.5, 1.75, 2];

export default function PodcastPlayer({ lessonId, label = 'Listen on the go' }) {
  const audioRef = useRef(null);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState(1);

  const streamUrl = `/api/media/podcast-stream/${lessonId}`;

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
    } else {
      a.play().catch((e) => setError(e.message));
    }
  };

  const seek = (delta) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    a.currentTime = Math.max(0, Math.min(duration, (a.currentTime || 0) + delta));
  };

  const onSliderChange = (e) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Number(e.target.value);
  };

  const cycleRate = () => {
    const idx = SPEEDS.indexOf(rate);
    const next = SPEEDS[(idx + 1) % SPEEDS.length];
    setRate(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  return (
    <div className="card-soft p-5 flex items-center gap-4 sm:gap-5">
      <div className="w-12 h-12 rounded-full bg-brand-aubergine/15 text-brand-aubergine flex items-center justify-center flex-shrink-0">
        <FaHeadphones />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] tracking-[0.18em] uppercase text-brand-aubergine">
          Companion podcast
        </div>
        <div className="font-display text-base text-ink truncate">{label}</div>
        {error ? (
          <p className="text-xs text-red-700 mt-1">{error}</p>
        ) : (
          <div className="mt-3 flex items-center gap-2 sm:gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => seek(-15)}
              disabled={!duration}
              aria-label="Back 15 seconds"
              className="text-slate2 hover:text-ink disabled:opacity-30"
            >
              <FaBackwardFast />
            </button>
            <button
              type="button"
              onClick={togglePlay}
              aria-label={playing ? 'Pause' : 'Play'}
              className="w-10 h-10 rounded-full bg-brand-deep text-bone flex items-center justify-center hover:bg-brand-aubergine transition-colors"
            >
              {playing ? (
                <FaPause className="text-xs" />
              ) : (
                <FaPlay className="text-xs translate-x-[1px]" />
              )}
            </button>
            <button
              type="button"
              onClick={() => seek(30)}
              disabled={!duration}
              aria-label="Forward 30 seconds"
              className="text-slate2 hover:text-ink disabled:opacity-30"
            >
              <FaForwardFast />
            </button>
            <input
              type="range"
              min="0"
              max={duration || 0}
              step="0.1"
              value={position}
              onChange={onSliderChange}
              disabled={!duration}
              aria-label="Seek"
              className="flex-1 min-w-[100px] h-1 accent-brand-deep"
            />
            <span className="text-[11px] text-slate2 tabular-nums w-20 text-right whitespace-nowrap">
              {formatTime(position)} / {formatTime(duration)}
            </span>
            <button
              type="button"
              onClick={cycleRate}
              aria-label="Playback speed"
              className="text-[11px] text-slate2 hover:text-ink w-10 text-right tabular-nums"
            >
              {rate}×
            </button>
          </div>
        )}
      </div>
      <audio
        ref={audioRef}
        src={streamUrl}
        preload="metadata"
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
        onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime || 0)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setPosition(0); }}
        onError={(e) => {
          const code = e.currentTarget?.error?.code;
          if (code !== 4) setError('Could not load audio. Please try again.');
        }}
      />
    </div>
  );
}
